import {Sidebar} from "./ui/sidebar";

import {SidebarInterface, BoundingBox, MapsConfig, ElementSelector, MapCenter} from "./dealer-service-point/interfaces"
import {getBoundingBox, resetBoundingBox, handleLocationError} from "./dealer-service-point/helpers"
import {addressBlock, infoBlock} from "./dealer-service-point/htmlblock";

export class dealerServicePoint {
  static sidebarInstance: SidebarInterface
  static map: google.maps.Map = null
  static marker: google.maps.Marker
  static markers: google.maps.Marker[] = []
  static infoWindow: google.maps.InfoWindow
  static boundingBox: BoundingBox = resetBoundingBox()
  static mapCenter: MapCenter
  static mapsConfig: MapsConfig
  static searchTerm = ""
  static dealers: Promise<any>

  static elementSelector: ElementSelector = {
    addresses: null,
    addressPlaceholder: null,
    geoLocation: document.querySelector('[data-js="dsp-search-geolocation"]'),
    mapCanvas: document.querySelector('[data-js="dsp-map-canvas"]'),
    mapsConfigElement: document.querySelector("script[data-maps-config]"),
    searchForm: document.querySelector('[data-js="dsp-search-form"]'),
    searchResults: document.querySelector('[data-js="dsp-search-results"]'),
    searchTerm: document.querySelector('[data-js="dsp-search-input"]'),
    selectedIdentifier: document.querySelector('[data-js="dsp-address-placeholder"][data-identifier]'),
    sidebar: document.querySelector('[data-js="dsp-sidebar"]'),
    toggleButton: document.querySelector('[data-js="dsp-toggle"]'),
    removeButton: document.querySelector('[data-js="dsp-remove"]') as HTMLAnchorElement,
  }

  static bindSelectAddress() {
    this.elementSelector.searchResults = document.querySelector('[data-js="dsp-search-results"]')
    this.elementSelector.addresses = document.querySelectorAll('[data-js="dsp-select-address"]')

    const selectAction = this.elementSelector.searchResults.dataset.selectAction

    this.elementSelector.addresses.forEach((address: HTMLElement) => {
      address.addEventListener("click", (event) => {
        event.preventDefault()

        return fetch(selectAction, {
          method: "POST",
          headers: {
            "content-type": "application/json; charset=UTF-8",
          },
          body: JSON.stringify({
            "dealer_id": address.dataset.addressId,
          }),
        })
          .then(response => {
            if (!response.ok) {
              throw new Error(response.statusText)
            }

            this.sidebarInstance.close()
            return response.json()
          })
          .then(() => {
            this.elementSelector.addressPlaceholder = document.querySelector('[data-js="dsp-address-placeholder"]')
            this.elementSelector.toggleButton = document.querySelector('[data-js="dsp-toggle"]')

            this.elementSelector.addressPlaceholder.dataset.identifier = address.dataset.addressId
            this.elementSelector.addressPlaceholder.innerHTML = addressBlock(
              {
                id: parseFloat(address.dataset.addresssId),
                name: address.dataset.dealerName,
                full_address: address.dataset.dealerAddress,
              },
              true,
            )
            this.elementSelector.toggleButton.innerText = this.elementSelector.toggleButton.dataset.changeText

            ;[].forEach.call(this.elementSelector.addresses, (previousAddress: HTMLElement) => {
              previousAddress.classList.remove("_active")
              previousAddress.classList.remove("_map-select")
            })
            address.classList.remove("_map-select")
            address.classList.add("_active")

            this.elementSelector.removeButton?.classList.remove("hidden")

            for (const marker of this.markers) {
              if (parseFloat(marker.getTitle()) === parseFloat(address.dataset.addressId)) {
                google.maps.event.trigger(marker, "click");
              }
            }
          })
      })
    })
  }

  private static getDealers() {
    const body: {search_term: string, bounding_box?: BoundingBox} = {
      "search_term": this.searchTerm ? this.searchTerm : "",
    }

    if (this.boundingBox && this.boundingBox.north) {
      body["bounding_box"] = this.boundingBox
    }

    this.dealers = fetch(this.elementSelector.searchForm.action, {
      method: "POST",
      headers: {
        "content-type": "application/json; charset=UTF-8",
      },
      body: JSON.stringify(body),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        return response.json()
      })
  }

  private static search() {
    let geocoder: google.maps.Geocoder
    const loadingBlock = infoBlock(true, this.elementSelector.searchResults.dataset.loading)
    const nothingBlock = infoBlock(false, this.elementSelector.searchResults.dataset.nothing)

    this.elementSelector.searchForm.addEventListener("submit", (event) => {
      event.preventDefault()

      this.elementSelector.searchResults.innerHTML = loadingBlock
      this.searchTerm = this.elementSelector.searchTerm.value.trim()
      this.boundingBox = resetBoundingBox()

      if (this.searchTerm) {
        geocoder = new google.maps.Geocoder()
        geocoder.geocode({"address": this.searchTerm})
          .then((response) => {
            if (response.results[0]) {
              const result = response.results[0]
              const latitude = result.geometry.location.lat()
              const longitude = result.geometry.location.lng()

              this.boundingBox = getBoundingBox(latitude, longitude)
              this.mapCenter = {
                lat: latitude,
                lng: longitude,
              }
              this.fillMap(nothingBlock)
            }
          })
          .catch((error) => {
            this.fillMap(nothingBlock)
            console.error(`Geocoder failed due to: ${error}`)
          })
      } else {
        this.fillMap(nothingBlock)
      }
    })
  }

  private static geoLocation() {
    if (this.elementSelector.geoLocation) {
      this.elementSelector.geoLocation.addEventListener("click", () => {
        const loadingBlock = infoBlock(true, this.elementSelector.searchResults.dataset.loading)
        const nothingBlock = infoBlock(false, this.elementSelector.searchResults.dataset.nothing)

        this.elementSelector.searchResults.innerHTML = loadingBlock

        if (!navigator.geolocation) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          handleLocationError(false, this.map, this.infoWindow, this.map.getCenter()!)
        }

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition((position) => {
            const latitude = position.coords.latitude
            const longitude = position.coords.longitude

            this.elementSelector.searchResults.innerHTML = loadingBlock
            this.boundingBox = getBoundingBox(latitude, longitude)
            this.mapCenter = {
              lat: latitude,
              lng: longitude,
            }
            this.fillMap(nothingBlock)

            this.elementSelector.geoLocation.classList.add("active")
          },
          () => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            handleLocationError(true, this.map, this.infoWindow, this.map.getCenter()!)
          })
        }
      })
    }
  }

  static dealerResults(htmlList: string, results: any) {
    for (const result of results) {
      const activeDealer = (
        this.elementSelector.selectedIdentifier
        && parseFloat(this.elementSelector.selectedIdentifier.dataset.identifier) === result.id
      )

      htmlList = `${htmlList}${addressBlock(result, false, activeDealer)}`
    }

    this.elementSelector.searchResults.innerHTML = htmlList

    this.markers = []
  }

  static fillMap(
    nothingBlock: any,
  ) {
    this.getDealers()

    if (!this.elementSelector.mapCanvas) {
      this.dealers
        .then(results => {
          const htmlList = results.length < 1 ? nothingBlock : ""

          this.dealerResults(htmlList, results)
          this.bindSelectAddress()
        })
    }

    if (this.elementSelector.mapCanvas) {
      this.maps()
    }
  }

  static resetAddresses(addresses: NodeList) {
    [].forEach.call(addresses, (removeSelectedAddress: HTMLElement) => {
      removeSelectedAddress.classList.remove("_map-select")
    })
  }

  static resetMarkers() {
    for (const marker of this.markers) {
      this.map.setZoom(this.mapsConfig.markerZoom)
      marker.setIcon(this.mapsConfig.icons.marker)
    }
  }

  private static maps() {
    const mapsConfig = this.mapsConfig
    const boundingBox = this.boundingBox
    const mapCenter = this.mapCenter
    const elementSelector = this.elementSelector

    let map = this.map
    let infoWindow = this.infoWindow
    let marker = this.marker
    const markers = this.markers
    const dealers = this.dealers

    const loadingBlock = infoBlock(true, elementSelector.searchResults.dataset.loading)
    const nothingBlock = infoBlock(false, elementSelector.searchResults.dataset.nothing)

    elementSelector.searchResults.innerHTML = loadingBlock

    function initMap() {
      map = new google.maps.Map(elementSelector.mapCanvas as HTMLElement, {
        zoom: mapsConfig.defaultZoom,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        center: new google.maps.LatLng(mapsConfig.defaultLocation.latitude, mapsConfig.defaultLocation.longitude),
        mapTypeControl: false,
      })
      infoWindow = new google.maps.InfoWindow()

      if (boundingBox && boundingBox.north) {
        if (mapsConfig.boundingBox.visible) {
          new google.maps.Rectangle({
            map,
            bounds: boundingBox,
            strokeColor: mapsConfig.boundingBox.strokeColor,
            strokeOpacity: mapsConfig.boundingBox.strokeOpacity,
            strokeWeight: mapsConfig.boundingBox.strokeWeight,
            fillColor: mapsConfig.boundingBox.fillColor,
            fillOpacity: mapsConfig.boundingBox.fillOpacity,
          })
        }

        map.setCenter(mapCenter)
        map.setZoom(mapsConfig.markerZoom)
      }

      dealers
        .then(dealer_data => {
          if (dealer_data.length < 1) {
            elementSelector.searchResults.innerHTML = nothingBlock
          }

          let htmlList = ""
          for (const dealer of dealer_data) {
            const activeDealer = (
              elementSelector.selectedIdentifier
              && parseFloat(elementSelector.selectedIdentifier.dataset.identifier) === dealer.id
            )
            let markerIndex = 0
            const latitude = dealer.geo_location.latitude
            const longitude = dealer.geo_location.longitude
            let skip_on_map = false

            if (typeof latitude !== "number" || typeof longitude !== "number") {
              console.warn(`Dealer ${dealer.name} has invalid geo location { lat: "${latitude}", lon: ${longitude} }`)
              skip_on_map = true
            }

            if (!skip_on_map) {
              marker = new google.maps.Marker({
                map,
                title: dealer.name.toString(),
                position: {
                  lat: latitude, lng: longitude,
                },
                icon: mapsConfig.icons.marker,
              })

              google.maps.event.addListener(marker, "click", (function (marker, dealer) {
                return function () {
                  const addresses: NodeList = document.querySelectorAll('[data-js="dsp-select-address"]')
                  const address: HTMLElement
                    = document.querySelector(`[data-js="dsp-select-address"][data-address-id="${dealer.id}"]`)

                  dealerServicePoint.resetAddresses(addresses)
                  dealerServicePoint.resetMarkers()

                  map.setZoom(mapsConfig.markerZoom)
                  map.setCenter(marker.getPosition() as google.maps.LatLng)
                  marker.setIcon(mapsConfig.icons.markerActive)
                  address.classList.add("_map-select")

                  address.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                  })
                }
              })(marker, dealer))

              markerIndex = markers.push(marker) - 1
            }

            htmlList = `${htmlList}${addressBlock(
              dealer,
              false,
              activeDealer,
              markerIndex,
              elementSelector.searchResults.dataset.labelSelectedOnMap)}`
          }

          elementSelector.searchResults.innerHTML = htmlList
          dealerServicePoint.bindSelectAddress()
        })

      return map
    }

    if (this.elementSelector.mapCanvas) {
      this.map = initMap()
    }
  }

  static selected(addresses: [{
    id: number,
    name: string,
    full_address: string
  }]) {
    const toggleButton: HTMLElement = document.querySelector('[data-js="dsp-toggle"]')
    const addressPlaceholder: HTMLElement = document.querySelector('[data-js="dsp-address-placeholder"]')
    let identifier = addressPlaceholder.dataset.identifier

    if (typeof identifier === "string" && (identifier === "None" || identifier === "null")) {
      if (identifier === "None") {
        console.warn('data-identifier is not json, please check syntax or add "tojson" filter.')
      }

      identifier = null
    }

    identifier = JSON.parse(identifier)

    if (identifier) {
      const selectedAddress = addresses.find(address => address.id === parseFloat(identifier))

      addressPlaceholder.innerHTML = addressBlock({
        id: parseFloat(identifier),
        name: selectedAddress.name,
        full_address: selectedAddress.full_address,
      }, true)

      toggleButton.innerText = toggleButton.dataset.changeText
    }
  }

  private static removeServicePoint(event: Event) {
    event.preventDefault()

    fetch(this.elementSelector.removeButton.href, {method: "POST"}).then(() => {

      this.elementSelector.addressPlaceholder = document.querySelector('[data-js="dsp-address-placeholder"]')
      this.elementSelector.toggleButton = document.querySelector('[data-js="dsp-toggle"]')

      this.elementSelector.addressPlaceholder.dataset.identifier = ""
      this.elementSelector.addressPlaceholder.innerHTML = ""
      this.elementSelector.toggleButton.innerText = this.elementSelector.toggleButton.dataset.selectText

      ;[].forEach.call(this.elementSelector.addresses, (address: HTMLElement) => {
        address.classList.remove("_active")
        address.classList.remove("_map-select")
      })
      this.elementSelector.removeButton.classList.add("hidden")
    })
  }

  static init() {
    if (this.elementSelector.sidebar && this.elementSelector.toggleButton) {
      this.sidebarInstance = new Sidebar(this.elementSelector.sidebar, 500)
      this.sidebarInstance.init()

      this.elementSelector.toggleButton.addEventListener("click", (event: { preventDefault: () => void; }) => {
        event.preventDefault()
        this.sidebarInstance.open()
      })
      this.elementSelector.removeButton?.addEventListener("click", this.removeServicePoint.bind(this))
      this.search()
      this.geoLocation()
      this.getDealers()

      this.dealers
        .then(results => {
          const htmlList = ""
          this.dealerResults(htmlList, results)
          this.selected(results)
          this.bindSelectAddress()
        })

      if (this.elementSelector.mapCanvas) {
        this.mapsConfig = JSON.parse(this.elementSelector.mapsConfigElement.innerHTML)

        this.maps()
      }
    }

    if (this.elementSelector.searchTerm) {
      let searchTerm = this.searchTerm
      let boundingBox = this.boundingBox

      this.elementSelector.searchTerm.addEventListener("change", function() {
        searchTerm = this.value.trim()
        boundingBox = resetBoundingBox()
      })
    }
  }
}
